/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { forwardRef, useRef } from 'react';
import { useSelector } from 'react-redux';

import classNames from 'classnames';

import { useTracker } from '@coursera/event-pulse/react';

import RichInputField, {
  type Props as RichInputFieldProps,
  type RichInputFieldRef,
} from 'bundles/ai-coach-platform/components/patterns/chat/common/RichInputField';
import type { SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { CoachAction } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import type { GlobalState } from '../store/types';

type Props = {
  placeholder: string;
  disableInput: boolean;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
  sendMessage: SendMessageFunc;
} & Omit<RichInputFieldProps, 'placeholder'>;

const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);

    .coach-chat-input-field {
      padding: var(--cds-spacing-200) 0 0 0;
    }
  `,
};

/**
 * Wrapper around `RichInputField` that handles the Redux store interaction and eventing for Coach chats
 */
const ChatRichInputField = forwardRef<RichInputFieldRef, Props>((props, richInputRef) => {
  const {
    sendMessage,
    placeholder,
    disableInput,
    disableSpeechToText,
    speechToTextDisabledTooltip,
    ...richInputFieldProps
  } = props;
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const refContainer = useRef<HTMLDivElement>(null);
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const handleSendMessageClick = (message: string) => {
    // ignore empty messages
    if (!message) {
      return;
    }

    sendMessage(message, CoachAction.TypedMessage);

    if (trackingContext) {
      // v3 tracking
      track('interact_coach', {
        coachInteractionType: 'send_message',
        coach: {
          mode: trackingContext.mode,
          location: 'chat',
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
          },
        }),
      });

      // This allows us to send the input message to amplitude as a separate event all together.
      // The main goal for now being to derive the character count of the input text as requested by product.
      track('send_coach_message', {
        coachMessageText: message,
        coachMessageLength: message.length,
        coach: {
          mode: trackingContext.mode,
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
          },
        }),
      });
    }
  };

  return (
    <div ref={refContainer} css={styles.container}>
      <div
        className={classNames('coach-chat-input-field', {
          'coach-message-disabled': isMessageLoading || disableInput,
        })}
      >
        <RichInputField
          ref={richInputRef}
          disableInput={disableInput}
          disableSpeechToText={disableSpeechToText}
          speechToTextDisabledTooltip={speechToTextDisabledTooltip}
          disabled={isMessageLoading || isMessageStreaming}
          placeholder={placeholder}
          data-testid="coach-chat-rich-input-field"
          onSend={handleSendMessageClick}
          {...richInputFieldProps}
        />
      </div>
    </div>
  );
});

export default ChatRichInputField;
